// TS twin of iOS `TopControlsView.swift`.
// Top bar — camera button left, close+done buttons right, wheel-info pill
// centered when brand+model are populated. Size dropdown is hidden behind
// SHOW_SIZE_DROPDOWN flag (default off, mirrors iOS feature flag).
//
// Visual language matches the ManualAdjustmentPanel footer:
// rgba(0,0,0,0.85) background with white icons/text. Header is
// pushed down HEADER_TOP_OFFSET_PX below the safe-area top.

import { useState, type ReactNode } from 'react'
import { ArrowRight, Camera, Settings, X } from 'lucide-react'
import type { WheelSize } from '@/models/wheel-size'
import { truncateModelName } from '@/lib/truncateModelName'
import { formatWheelDiameter } from '@/lib/formatWheelDiameter'
import { Spinner } from '@/components/spinner/spinner'

const HEADER_TOP_OFFSET_PX = 25
const DARK_PANEL_BG = 'rgba(0, 0, 0, 0.85)'

export interface TopControlsWheelInfo {
  brandName: string
  modelName: string
  brandLogoURL: string | null
  /** Dark-mode brand mark, when the brand has one. */
  brandLogoDarkURL: string | null
  sizes: readonly WheelSize[]
  selectedSize: WheelSize | null
  /** Diameter the wheel currently RENDERS at — payload size × the live
   *  scale multiplier, so a pinch-snapped wheel reads its new whole
   *  inch here. Null when the payload carries no size: the segment is
   *  absent rather than a guess (2026-09-07). */
  displayDiameterInches: number | null
}

interface TopControlsViewProps {
  showDoneButton: boolean
  wheelInfo: TopControlsWheelInfo | null
  closeTitle: string
  onClose: () => void
  onCamera: () => void
  /** Opens the settings popover. Replaced the reload button, which
   *  restarted the whole AR session — a dev-era escape hatch sitting
   *  one icon from Close and meaning nothing to a shopper. */
  onSettings?: () => void
  onDone: () => void
  onWheelInfoTap: () => void
  onSelectSize: (size: WheelSize) => void
  isLandscape?: boolean
  /** Close was pressed and teardown is running. */
  closing?: boolean
  /** v2 UI: pill in upper-LEFT, X/Refresh/Camera as a vertical stack
   *  in the upper-RIGHT. Set when App is running under ?controlChips=1. */
  v2Layout?: boolean
}

// Mirrors iOS TopControlsFeatureFlags.showSizeDropdown — off until the
// per-size model download flow exists. Flip via Vite env at build time.
const SHOW_SIZE_DROPDOWN = false

export function TopControlsView({
  showDoneButton,
  wheelInfo,
  closeTitle,
  onClose,
  onCamera,
  onSettings,
  onDone,
  onWheelInfoTap,
  onSelectSize,
  isLandscape = false,
  v2Layout = false,
  closing = false,
}: TopControlsViewProps) {
  const shouldShowPill =
    wheelInfo != null &&
    wheelInfo.brandName.length > 0 &&
    wheelInfo.modelName.length > 0

  const sizes = wheelInfo?.sizes ?? []
  const sizesArray: WheelSize[] = [...sizes]

  if (v2Layout) {
    return (
      <header
        data-orientation={isLandscape ? 'landscape' : 'portrait'}
        data-testid="top-controls-v2"
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          padding: '35px 10px 0px 5px',
          paddingTop: 8 + HEADER_TOP_OFFSET_PX,
          color: 'white',
        }}
      >
        {shouldShowPill ? (
          <button
            type="button"
            onClick={onWheelInfoTap}
            data-testid="wheel-info-pill"
            style={{
              background: DARK_PANEL_BG,
              color: 'white',
              border: 'none',
              // Matches the camera / close icon buttons flanking it.
              height: 39,
              borderRadius: 8,
              // No inset: the plate runs edge to edge.
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              // Same lift as the icon buttons flanking it.
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              marginTop: -3,
              left: 0,
              // No rotation — the outer fixed-positioned TL container
              // in App is what orients this whole pill for landscape.
            }}
          >
            <WheelInfoPillContent wheelInfo={wheelInfo} />
          </button>
        ) : (
          <div />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <CircleIconButton
            ariaLabel={closeTitle}
            onClick={onClose}
            busy={closing}
          >
            <RotatedGlyph landscape={isLandscape}>
              <X size={20} strokeWidth={2} />
            </RotatedGlyph>
          </CircleIconButton>
          <CircleIconButton
            ariaLabel="Settings"
            onClick={() => onSettings?.()}
          >
            <RotatedGlyph landscape={isLandscape}>
              <Settings size={20} strokeWidth={2} />
            </RotatedGlyph>
          </CircleIconButton>
          <CircleIconButton
            ariaLabel="Enter Camera Mode"
            onClick={onCamera}
          >
            <RotatedGlyph landscape={isLandscape}>
              <Camera size={20} strokeWidth={2} />
            </RotatedGlyph>
          </CircleIconButton>
        </div>
      </header>
    )
  }

  return (
    <header
      data-orientation={isLandscape ? 'landscape' : 'portrait'}
      style={{
        position: 'relative',
        display: 'flex',
        // flex-start so the right-side X button stays at the top row and
        // doesn't center against the taller left column (camera + reload).
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        padding: '8px 20px',
        paddingTop: 8 + HEADER_TOP_OFFSET_PX,
        color: 'white',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <CircleIconButton
          ariaLabel="Enter Camera Mode"
          onClick={onCamera}
        >
          <Camera size={20} strokeWidth={2} />
        </CircleIconButton>
        <CircleIconButton
          ariaLabel="Settings"
          onClick={() => onSettings?.()}
        >
          <Settings size={20} strokeWidth={2} />
        </CircleIconButton>
      </div>

      {shouldShowPill && (
        <WheelInfoPill wheelInfo={wheelInfo} onTap={onWheelInfoTap} />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {SHOW_SIZE_DROPDOWN && sizesArray.length > 0 && (
          <SizeDropdown
            sizes={sizesArray}
            selected={wheelInfo?.selectedSize ?? null}
            onSelect={onSelectSize}
          />
        )}
        <CircleIconButton ariaLabel={closeTitle} onClick={onClose} busy={closing}>
          <X size={20} strokeWidth={2} />
        </CircleIconButton>
        {showDoneButton && (
          <button
            type="button"
            onClick={onDone}
            style={{
              background: '#3ec45b',
              color: 'white',
              border: 'none',
              borderRadius: 999,
              padding: '10px 16px',
              fontSize: 16,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            Done <ArrowRight size={16} strokeWidth={2.5} aria-hidden />
          </button>
        )}
      </div>
    </header>
  )
}

interface CircleIconButtonProps {
  /** Swap the icon for a spinner and stop taking taps. For work that
   *  blocks the main thread — tearing down an XR session freezes the
   *  phone for a beat, and with no mark on the button the tap reads as
   *  having missed (2026-08-24 LUAT). */
  busy?: boolean
  ariaLabel: string
  onClick: () => void
  size?: number
  children: ReactNode
  /** Override the default dark background — used by the delete icon
   *  in the v2 floating row (keeps the same shape, custom colour). */
  color?: string
}

/** No-op wrapper. Kept only for API compatibility; landscape orientation
 *  is now handled by the outer fixed-positioned containers in App
 *  (each container rotates as one unit to face the user). */
export function RotatedGlyph({
  landscape,
  children,
}: {
  landscape: boolean
  children: ReactNode
}) {
  void landscape
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </span>
  )
}

export function CircleIconButton({
  ariaLabel,
  onClick,
  size = 39,
  children,
  color,
  busy = false,
}: CircleIconButtonProps) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-busy={busy || undefined}
      data-busy={busy ? 'true' : undefined}
      // Refuse further taps while busy: the work is already under way
      // and a second one can only queue another teardown.
      disabled={busy}
      onClick={busy ? undefined : onClick}
      style={{
        width: size,
        height: size,
        // Matches joystick RepeatButton radius so all round-ish icons
        // in the UI share the same corner language.
        borderRadius: 8,
        background: DARK_PANEL_BG,
        color: color ?? 'white',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      }}
    >
      {busy ? <Spinner size={Math.round(size * 0.5)} /> : children}
    </button>
  )
}

/** Pill contents shared by both header layouts (centered + v2).
 *
 *  With a brand logo: the logo replaces the brand NAME to the left of
 *  the model name, splitting the pill 30% logo / 70% name — the logo
 *  already rode all the way from the assembler
 *  (base_part.brand.logo → WheelDetails.brandLogoURL) and was simply
 *  never rendered. Without a logo — or if it fails to load — the pill
 *  falls back to the original "Brand Model" text so brand identity is
 *  never silently lost. */
function WheelInfoPillContent({
  wheelInfo,
}: {
  wheelInfo: TopControlsWheelInfo
}) {
  const [logoFailed, setLogoFailed] = useState(false)
  // The pill chrome is dark, so prefer the brand's DARK mark. When the
  // brand only ships a light mark (AutoViz today) its black wordmark
  // would vanish into the pill — plate that one on white instead.
  const darkURL = wheelInfo.brandLogoDarkURL
  const hasDark = darkURL != null && darkURL.length > 0
  const logoURL = hasDark ? darkURL : wheelInfo.brandLogoURL
  const needsPlate = !hasDark
  const showLogo = logoURL != null && logoURL.length > 0 && !logoFailed
  // "Model | 17"" — the size the wheel currently renders at. Absent
  // (no separator either) when the payload carried no size.
  const sizeLabel =
    wheelInfo.displayDiameterInches != null
      ? `${formatWheelDiameter(wheelInfo.displayDiameterInches)}"`
      : null

  if (!showLogo) {
    // The pill carries no padding of its own so a logo plate can run edge
    // to edge, which left the text fallback flush against the border with
    // the letters touching it (2026-09-04). Pad the TEXT rather than the
    // pill, so the logo layout below is untouched.
    //
    // The row stretches so the size segment's divider can run the pill's
    // full height; the size sits OUTSIDE the padded text span so its own
    // inset from the rule is the same on both layouts.
    return (
      <span
        style={{ display: 'flex', alignItems: 'stretch', height: '100%' }}
      >
        <span
          data-testid="wheel-info-text"
          style={{
            paddingLeft: 3,
            paddingRight: 3,
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {wheelInfo.brandName} {wheelInfo.modelName}
        </span>
        {sizeLabel != null && <WheelInfoSizeSegment label={sizeLabel} />}
      </span>
    )
  }

  return (
    <span
      style={{
        display: 'flex',
        // stretch (not center): the logo plate fills the pill's full
        // inner height so it reads as one flush chip, no gutters.
        alignItems: 'stretch',
        // DEFINITE width: the 30/70 flex-bases below are percentages,
        // and a percentage inside a shrink-to-fit <button> resolves
        // against a width derived from the content itself — the logo
        // would size its own slot. A fixed row width makes the split
        // mean exactly what it says on every phone.
        width: 240,
        maxWidth: '56vw',
        minWidth: 0,
        height: '100%',
      }}
    >
      <span
        data-testid="wheel-info-logo-slot"
        style={{
          flexBasis: '35%',
          flexGrow: 0,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Left corners follow the pill's radius (8) minus its 1px
          // padding; the RIGHT side stays square so the plate butts
          // straight into the model name with no seam.
          borderRadius: '8px 0px 0px 8px',
          overflow: 'hidden',
          // Breathing room so the mark never touches the plate edges.
          // Left carries 2px more: the pill's rounded left corner eats
          // into that side, so equal padding reads as off-centre.
          paddingLeft: 5,
          paddingRight: 3,
          // Light-only marks get a white plate; dark marks sit
          // directly on the pill so it reads as one solid chip.
          ...(needsPlate ? { background: 'white' } : {}),
        }}
      >
        <img
          data-testid="wheel-info-logo"
          src={logoURL}
          alt={wheelInfo.brandName}
          onError={() => setLogoFailed(true)}
          style={{
            maxWidth: '100%',
            maxHeight: 20,
            // Brand marks vary wildly in aspect ratio — contain keeps
            // every one of them undistorted inside its 30% slot.
            objectFit: 'contain',
          }}
        />
      </span>
      <span
        data-testid="wheel-info-name"
        style={{
          position: 'relative',
          flexBasis: '65%',
          flexGrow: 1,
          minWidth: 0,
          // The pill itself is padding-free (1px) so the plate can
          // reach its edges — the label carries its own inset, and
          // flex-centers itself vertically.
          display: 'flex',
          alignItems: 'center',
          padding: '0 10px',
          textAlign: 'left',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          // Fade the label out instead of chopping it with an ellipsis.
          // A MASK (not a colored overlay) is the right tool here: the
          // pill background is translucent over the live camera feed,
          // so an opaque gradient would darken the edge rather than
          // blend into it. Masking the text lets the pill's own
          // background show through exactly.
          maskImage:
            'linear-gradient(to right, #000 78%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to right, #000 78%, transparent 100%)',
        }}
      >
        {/* 15px white wash BEHIND the label so the plate's edge melts
            into the dark text area instead of ending in a hard line.
            Only when the plate is actually white — a dark mark sits
            straight on the pill and needs no blend.
            Starts 1px INSIDE the plate (left:-1, width:16) because the
            35% flex-basis of a 56vw row lands on a fractional pixel:
            plate-edge and wash-edge rounded to different pixels and the
            dark pill bled through as a hairline. Both sides are pure
            white across the overlap, so it is invisible. */}
        {needsPlate && (
          <span
            data-testid="wheel-info-plate-bleed"
            aria-hidden
            style={{
              position: 'absolute',
              left: -1,
              top: 0,
              bottom: 0,
              width: 16,
              background: 'linear-gradient(to right, white, transparent)',
              pointerEvents: 'none',
            }}
          />
        )}
        <span style={{ position: 'relative' }}>
          {truncateModelName(wheelInfo.modelName)}
        </span>
      </span>
      {/* Size segment OUTSIDE the masked name slot: a long model name
          fades out before the separator instead of swallowing the size
          with it. */}
      {sizeLabel != null && <WheelInfoSizeSegment label={sizeLabel} />}
    </span>
  )
}

/** The pill's size segment, and the divider in front of it.
 *
 *  The divider is this segment's own LEFT BORDER, not a "|" glyph. A glyph
 *  sits on the text baseline, so it was shorter than the pill and sat off
 *  its vertical centre, with the size butting straight into it. A border
 *  runs the full height of the stretched row by construction, and at half
 *  opacity it reads as a rule over the pill rather than as another
 *  character in the label. */
function WheelInfoSizeSegment({ label }: { label: string }) {
  return (
    <span
      data-testid="wheel-info-size"
      style={{
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        height: '100%',
        whiteSpace: 'nowrap',
        borderLeft: '1px solid rgba(255, 255, 255, 0.5)',
        paddingLeft: 10,
        paddingRight: 10,
        flexShrink: 0,
      }}
    >
      {label}
    </span>
  )
}

interface WheelInfoPillProps {
  wheelInfo: TopControlsWheelInfo
  onTap: () => void
}

function WheelInfoPill({ wheelInfo, onTap }: WheelInfoPillProps) {
  return (
    <button
      type="button"
      onClick={onTap}
      data-testid="wheel-info-pill"
      style={{
        position: 'absolute',
        left: '50%',
        // Flush with the icon buttons: same offset the header applies
        // to them. (Historic +7 nudge dated from 44-px buttons; they
        // are 39 px now — the pill's own height — so it only pushed
        // the pill out of alignment.)
        top: 8 + HEADER_TOP_OFFSET_PX,
        transform: 'translateX(-50%)',
        background: DARK_PANEL_BG,
        color: 'white',
        border: 'none',
        // Matches the camera / close icon buttons flanking it.
        height: 39,
        borderRadius: 8,
        // No inset: the plate runs edge to edge (a 1px ring read as a
        // border around it).
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        // Same lift as the icon buttons flanking it.
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
      }}
    >
      <WheelInfoPillContent wheelInfo={wheelInfo} />
    </button>
  )
}

interface SizeDropdownProps {
  sizes: WheelSize[]
  selected: WheelSize | null
  onSelect: (size: WheelSize) => void
}

function SizeDropdown({ sizes, selected, onSelect }: SizeDropdownProps) {
  const label = (selected ?? sizes[0])?.display ?? '—'
  return (
    <select
      aria-label="Wheel size"
      value={selected?.display ?? sizes[0]?.display ?? ''}
      onChange={(e) => {
        const next = sizes.find((s) => s.display === e.target.value)
        if (next != null) onSelect(next)
      }}
      disabled={sizes.length <= 1}
      style={{
        background: 'rgba(255, 255, 255, 0.2)',
        color: 'white',
        border: 'none',
        borderRadius: 999,
        padding: '0 14px',
        height: 44,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {sizes.map((s) => (
        <option key={s.display} value={s.display}>
          {s.display === label ? `✓ ${s.display}` : s.display}
        </option>
      ))}
    </select>
  )
}
